import React from 'react';
import {
  ApartmentOutlined,
  AppstoreOutlined,
  BookOutlined,
  CalculatorOutlined,
  CalendarOutlined,
  CarryOutOutlined,
  CloudUploadOutlined,
  CoffeeOutlined,
  FileSearchOutlined,
  ControlOutlined,
  CrownOutlined,
  CustomerServiceOutlined,
  MessageOutlined,
  SnippetsOutlined,
  DashboardOutlined,
  FieldTimeOutlined,
  FileProtectOutlined,
  FlagOutlined,
  FundProjectionScreenOutlined,
  GiftOutlined,
  SketchOutlined,
  SafetyCertificateOutlined,
  HourglassOutlined,
  IdcardOutlined,
  KeyOutlined,
  LineChartOutlined,
  LikeOutlined,
  NotificationOutlined,
  PictureOutlined,
  ProfileOutlined,
  ReadOutlined,
  ScheduleOutlined,
  SettingOutlined,
  ShopOutlined,
  SafetyOutlined,
  SolutionOutlined,
  SoundOutlined,
  StarOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  ToolOutlined,
  TrophyOutlined,
  UnorderedListOutlined,
  UserAddOutlined,
  UserOutlined,
  VideoCameraOutlined,
  WalletOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import type { Tone } from 'theme/tokens';

/**
 * NGUỒN DUY NHẤT cho điều hướng của trang Quản trị: thanh bên, đường dẫn (breadcrumb),
 * ô "Tìm chức năng", tiêu đề/mô tả trang và lối tắt ở Tổng quan đều đọc từ đây.
 * Thêm trang mới: thêm route ở routes/routes.tsx rồi thêm một dòng vào nhóm phù hợp.
 *
 * Nhóm theo CÔNG VIỆC của người quản lý (không còn nhóm "Quản lý chung" gom lẫn lộn).
 * `key` = đường dẫn KHÔNG có dấu "/" đầu (giữ nguyên URL cũ để bookmark không gãy).
 */
export interface NavItem {
  key: string;
  label: string;
  icon?: React.ReactNode;
  /** Một câu: trang này để làm gì — hiện dưới tiêu đề trang và trong ô tìm kiếm. */
  description: string;
  /** Từ khoá thêm cho ô tìm kiếm (tên cũ, tiếng Anh...). */
  keywords?: string;
  /** API của trang chỉ cho ADMIN → ẩn với MOD (và chặn nếu MOD gõ thẳng URL). */
  adminOnly?: boolean;
  /**
   * Quyền riêng do Admin cấp cho từng người (không theo vai trò): mục chỉ hiện khi quyền đó
   * là `true` trong `perms`. 'dataImport' = được nhập dữ liệu (useCurrentRoles().canImportData).
   * 'onlineReward' = được xem Thưởng Online (useCurrentRoles().canViewOnlineReward).
   * 'equipmentQuote' = được dùng Báo giá thiết bị: KTV hoặc MOD được cấp (useCurrentRoles().canUseEquipmentQuote).
   */
  requires?: 'dataImport' | 'onlineReward' | 'equipmentQuote';
  /**
   * Người xem thuộc các nhóm này thấy mục dù thiếu quyền `requires`: trang tự cắt số liệu theo phạm vi
   * của họ (vd Tra cứu KC: Quản lý Studio xem Creator Studio mình). Vẫn phải qua `audience`.
   */
  requiresExcept?: NavAudience[];
  /** Không hiện trên thanh bên (trang con mở từ trang khác) nhưng vẫn có breadcrumb. */
  hidden?: boolean;
  /** Trang cha để breadcrumb/đánh dấu menu khi đang ở trang con. */
  parent?: string;
  /**
   * Trang cũ nay là MỘT TAB của trang khác (route cũ tự chuyển sang `?tab=`).
   * Không hiện trên thanh bên nhưng vẫn tìm được bằng ô "Tìm chức năng".
   */
  tabOf?: string;
  /**
   * Ai thấy mục này (Studio, docs/studio-quan-ly/SPEC-TRIEN-KHAI-STUDIO.md §8.2). Không khai: theo
   * `adminOnly` / `requires` như cũ, và Quản lý Studio KHÔNG thấy.
   */
  audience?: NavAudience[];
}

/** ADMIN: có vai trò ADMIN · STUDIO: Quản lý Studio · MOD: mọi MOD còn lại (SMOD, Quản lý chưa có Studio). */
export type NavAudience = 'ADMIN' | 'MOD' | 'STUDIO';

export interface NavGroup {
  key: string;
  label: string;
  /** Mô tả ngắn cho thẻ nhóm ở trang Tổng quan. */
  description: string;
  icon: React.ReactNode;
  tone: Tone;
  items: NavItem[];
}

export const HOME_ITEM: NavItem = {
  key: '',
  label: 'Tổng quan',
  description: 'Việc cần xử lý hôm nay và lối tắt tới các chức năng hay dùng.',
  keywords: 'dashboard trang chu home',
};

export const NAV_GROUPS: NavGroup[] = [
  {
    key: 'people',
    label: 'Thành viên',
    description: 'Tài khoản, vai trò, UID TikTok và xác nhận nhân viên.',
    icon: <TeamOutlined />,
    tone: 'accent',
    items: [
      {
        key: 'users-management',
        icon: <UserOutlined />,
        label: 'Tài khoản',
        description:
          'Tìm, tạo, sửa tài khoản; vai trò, phòng ban, UID TikTok; mở nhanh công, phép, lương, Sao của từng người.',
        keywords: 'user nguoi dung tai khoan thanh vien idol creator agency tiktok uid',
        adminOnly: true,
      },
      {
        // Chỉ ADMIN: mọi API staff-access/admin/* đều khoá ADMIN
        // (docs/to-chuc-lai-nhom-quyen/00-ban-do-va-phuong-an-2026-09-29.md §5.2).
        key: 'staff-confirm',
        icon: <SolutionOutlined />,
        label: 'Xác nhận nhân viên',
        description:
          'Duyệt người xin làm nhân viên hoặc đổi phòng ban; rà soát người đã tự khai trước đây.',
        keywords: 'nhan vien xac nhan phong ban duyet tu khai staff',
        adminOnly: true,
      },
      {
        key: 'domestic-management',
        icon: <CrownOutlined />,
        label: 'Nhóm quản lý',
        description: 'Danh sách nhóm, trưởng nhóm; thêm thành viên ngay trên từng nhóm.',
        keywords: 'gia toc domestic group nhom family',
        adminOnly: true,
      },
      {
        key: 'domestic-user',
        label: 'Thêm thành viên nhóm',
        description: 'Chọn nhóm rồi thêm thành viên (có thể thêm ngay từ danh sách Nhóm quản lý).',
        hidden: true,
        adminOnly: true,
        parent: 'domestic-management',
      },
      {
        key: 'group/:id/management',
        label: 'Chi tiết nhóm',
        description: 'Thông tin và thành viên của nhóm quản lý.',
        hidden: true,
        adminOnly: true,
        parent: 'domestic-management',
      },
      {
        key: 'pick-idol',
        label: 'Ghép Creator',
        description: 'Ghép Creator với hội thoại.',
        hidden: true,
        adminOnly: true,
      },
      {
        key: 'request-idol-management',
        label: 'Duyệt Creator mới',
        description: 'Yêu cầu đăng ký Creator từ bản app cũ.',
        hidden: true,
        adminOnly: true,
      },
    ],
  },
  {
    // Key nhóm 'access' khác key mục con ('access/groups', 'access/permissions') — antd Menu
    // cần không trùng. ADMIN + MOD đều vào được; MOD chỉ xem.
    key: 'access',
    label: 'Nhóm & quyền',
    description: 'Mỗi nhóm người là ai, được làm gì; bảng quyền của các cấp quản lý.',
    icon: <SafetyOutlined />,
    tone: 'warning',
    items: [
      {
        key: 'access/groups',
        icon: <ApartmentOutlined />,
        label: 'Nhóm người',
        description:
          'Mười nhóm người trong hệ thống: cách nhận biết, số người, và mỗi nhóm được làm gì.',
        keywords:
          'nhom nguoi vai tro loai thanh vien role group admin van hanh ktv creator npc user',
      },
      {
        key: 'access/permissions',
        icon: <KeyOutlined />,
        label: 'Quyền hạn',
        description:
          'Bật, tắt từng việc cho Admin, Quản lý tổng và Quản lý vận hành. Mỗi lần đổi đều ghi lại.',
        keywords: 'quyen han phan quyen permission matrix smod quan ly van hanh',
      },
    ],
  },
  {
    // Studio (docs/studio-quan-ly/SPEC-TRIEN-KHAI-STUDIO.md §8.2). Key nhóm khác mọi key mục con.
    key: 'studio-center',
    label: 'Studio',
    description:
      'Studio, Quản lý, Creator, Nhóm nhảy, kim cương và việc cần xử lý của từng Studio.',
    icon: <CrownOutlined />,
    tone: 'purple',
    items: [
      {
        key: 'studios',
        icon: <CrownOutlined />,
        label: 'Studio',
        description: 'Danh sách Studio, đề nghị chờ duyệt và giám sát cờ, nhật ký của mọi Studio.',
        keywords: 'studio nhom quan ly gia toc domestic group family giam sat co',
        audience: ['ADMIN', 'MOD'],
      },
      {
        key: 'studio',
        icon: <TeamOutlined />,
        label: 'Studio của tôi',
        description: 'Creator, Nhóm nhảy, kim cương và việc cần xử lý của Studio bạn quản lý.',
        keywords: 'studio cua toi quan ly',
        audience: ['STUDIO'],
      },
      {
        key: 'studio/:id',
        label: 'Chi tiết Studio',
        description:
          'Tổng quan, Thành viên, Nhóm nhảy, Kim cương, Cần xử lý, Lịch sử của một Studio.',
        hidden: true,
        parent: 'studios',
        audience: ['ADMIN', 'MOD', 'STUDIO'],
      },
      // Ba mục cũ domestic-management, domestic-user, group/:id/management chuyển vào đây ở lượt I2b
      // (SPEC §8.1), cùng lúc gỡ khỏi nhóm `people`; tới lúc đó chúng vẫn trỏ trang cũ đang chạy.
    ],
  },
  {
    // Khác key mục con 'support' — antd Menu cần key nhóm và key mục không trùng.
    key: 'support-center',
    label: 'Hỗ trợ',
    description: 'Trả lời tin nhắn Creator, Nhân viên, fan gửi “HP Media Hỗ trợ” trong app; văn mẫu, ghi chú.',
    icon: <CustomerServiceOutlined />,
    tone: 'info',
    items: [
      {
        key: 'support',
        icon: <MessageOutlined />,
        label: 'Hộp thư hỗ trợ',
        description:
          'Tin người dùng nhắn “HP Media Hỗ trợ” trong app. Nhận xử lý, trả lời (văn mẫu, ảnh), ghi chú nội bộ, đánh dấu đã xong.',
        keywords: 'chat tin nhan ho tro cskh inbox support helpdesk zalo',
        audience: ['ADMIN', 'MOD', 'STUDIO'],
      },
      {
        key: 'support/manage',
        icon: <SnippetsOutlined />,
        label: 'Văn mẫu & cài đặt',
        description:
          'Văn mẫu trả lời nhanh; tài khoản hỗ trợ, tin chào, giờ trực, tin tự động ngoài giờ; mở kênh hàng loạt.',
        keywords: 'van mau template tra loi nhanh gio truc tu dong cai dat ho tro',
      },
    ],
  },
  {
    key: 'hr',
    label: 'Nhân sự',
    description: 'Chấm công, ca làm, bộ phận, nghỉ phép, tăng ca và phiếu lương.',
    icon: <CalendarOutlined />,
    tone: 'purple',
    items: [
      {
        key: 'attendance-management',
        icon: <FieldTimeOutlined />,
        label: 'Chấm công',
        description: 'Bảng công theo ngày; sửa công, chấm hộ, xem lịch sử, xuất bảng công & lương.',
        keywords: 'attendance cham cong check in bang cong',
        adminOnly: true,
      },
      {
        key: 'shift-management',
        icon: <ScheduleOutlined />,
        label: 'Ca làm',
        description: 'Khung giờ ca làm theo phòng ban.',
        keywords: 'shift ca kip lich lam',
      },
      {
        // Trang của hydiamond (API /departments). Xem: mọi Admin/MOD; thêm/sửa/xoá: chỉ ADMIN
        // (trang tự kiểm qua auth/status, backend chặn bằng RolesGuard).
        key: 'admin/attendance/departments',
        icon: <ApartmentOutlined />,
        label: 'Bộ phận',
        description: 'Danh mục bộ phận (phòng ban) dùng khi gán nhân viên, ca làm và lọc chấm công.',
        keywords: 'department phong ban bo phan quan ly phong ban',
      },
      {
        key: 'admin/attendance/leave-requests',
        icon: <CoffeeOutlined />,
        label: 'Nghỉ phép',
        description: 'Duyệt đơn nghỉ phép; tab Loại nghỉ phép để thêm/sửa loại nghỉ.',
        keywords: 'leave nghi phep don duyet',
      },
      {
        key: 'admin/attendance/leave-types',
        label: 'Loại nghỉ phép',
        description: 'Các loại nghỉ (phép năm, ốm...) — nằm trong trang Nghỉ phép.',
        keywords: 'leave type loai nghi',
        hidden: true,
        tabOf: 'admin/attendance/leave-requests',
      },
      {
        key: 'overtime-management',
        icon: <HourglassOutlined />,
        label: 'Tăng ca',
        description: 'Duyệt đơn tăng ca (OT) theo người, trạng thái và thời gian.',
        keywords: 'overtime ot tang ca',
      },
      {
        key: 'payslips-management',
        icon: <WalletOutlined />,
        label: 'Phiếu lương',
        description: 'Xuất bảng công & lương tháng (Excel); tạo, sửa và chốt phiếu lương.',
        keywords: 'payslip luong salary',
      },
    ],
  },
  {
    key: 'star',
    label: 'Điểm Sao',
    description: 'Nhiệm vụ, tài khoản Sao, cửa hàng đổi quà và phạt.',
    icon: <StarOutlined />,
    tone: 'star',
    items: [
      {
        key: 'star/dashboard',
        icon: <StarOutlined />,
        label: 'Tổng quan Sao',
        description: 'Sao phát ra, tiêu dùng và việc chờ duyệt.',
        keywords: 'star diem sao',
      },
      {
        key: 'star/accounts',
        icon: <IdcardOutlined />,
        label: 'Tài khoản Sao',
        description: 'Số dư từng người; cộng/trừ Sao thủ công.',
        keywords: 'star account so du',
      },
      {
        key: 'star/missions',
        icon: <FlagOutlined />,
        label: 'Nhiệm vụ',
        description: 'Nhiệm vụ tích Sao và hàng đợi duyệt báo cáo.',
        keywords: 'mission nhiem vu',
      },
      {
        key: 'star/recruits',
        icon: <UserAddOutlined />,
        label: 'Tuyển dụng (HR)',
        description:
          'Phiếu tuyển Creator Studio: 3 mốc Đạt chuẩn, Giữ chân, Nổi bật; xác nhận tay và huỷ phiếu.',
        keywords: 'tuyen dung hr recruit phieu tuyen creator studio',
      },
      {
        key: 'star/dance',
        icon: <CarryOutOutlined />,
        label: 'Chấm điểm nhảy',
        description: 'Giám sát buổi giáo viên chấm điểm Creator Dance; huỷ buổi để thu hồi Sao.',
        keywords: 'cham diem nhay dance giao vien buoi tap kiem tra bai',
      },
      {
        key: 'star/shop',
        icon: <ShopOutlined />,
        label: 'Cửa hàng & đơn',
        description: 'Vật phẩm đổi Sao và đơn chờ giao.',
        keywords: 'shop cua hang doi qua don',
      },
      {
        key: 'star/penalties',
        icon: <WarningOutlined />,
        label: 'Phạt',
        description: 'Ghi phạt, loại phạt và yêu cầu xoá phạt bằng Sao.',
        keywords: 'penalty phat',
        audience: ['ADMIN', 'MOD', 'STUDIO'],
      },
      {
        key: 'star/ledger',
        icon: <BookOutlined />,
        label: 'Lịch sử giao dịch',
        description: 'Từng lần cộng/trừ Sao của mọi người — ghi lại để đối chiếu, không sửa được.',
        keywords: 'ledger so cai so tong hop lich su giao dich',
      },
      {
        key: 'star/config',
        icon: <ControlOutlined />,
        label: 'Cấu hình Sao',
        description:
          'Mốc KC, chuỗi tuần, công chuẩn, hạn mức; ca chuẩn, thâm niên, lịch live, tuyển dụng, chấm nhảy.',
        keywords: 'config cau hinh',
        adminOnly: true,
      },
    ],
  },
  {
    // Key nhóm khác key mục con ('revenue-management') — antd Menu cần không trùng.
    // URL các trang giữ nguyên (star/kc, revenue-management) để bookmark không gãy.
    key: 'revenue',
    label: 'Doanh thu',
    description:
      'File TikTok nhập một chỗ ở Nhập dữ liệu; Data Kim cương và Bảng xếp hạng chỉ để xem.',
    icon: <LineChartOutlined />,
    tone: 'success',
    items: [
      {
        // Chỉ hiện với người được nhập: ADMIN, hoặc MOD đã được Admin cấp quyền
        // (docs/nhap-du-lieu/DAC-TA-2026-09-29.md §9.1).
        key: 'data-import',
        icon: <CloudUploadOutlined />,
        label: 'Nhập dữ liệu',
        description:
          'Nhập file TikTok theo ngày và theo tháng: Kim cương, giờ LIVE và các chỉ số của Creator.',
        keywords: 'nhap du lieu import file tiktok data ngay thang kim cuong upload excel csv',
        requires: 'dataImport',
      },
      {
        key: 'star/kc',
        icon: <SketchOutlined />,
        label: 'Data Kim cương',
        description:
          'Xem KC theo ngày của từng Creator và kết quả chấm Sao. Trang chỉ để xem; số liệu nhập ở Nhập dữ liệu.',
        keywords: 'kc kim cuong ngay diamond doanh thu data',
      },
      {
        // Quyền như Nhập dữ liệu: ADMIN, MOD được Admin cho phép (docs/tra-cuu-kc/00-nghien-cuu-2026-10-27.md §4).
        // Đợt 2: Quản lý Studio cũng thấy, chỉ xem Creator Studio mình (API cắt theo phạm vi).
        key: 'kc-lookup',
        audience: ['ADMIN', 'MOD', 'STUDIO'],
        requiresExcept: ['STUDIO'],
        icon: <FileSearchOutlined />,
        label: 'Tra cứu KC',
        description:
          'Dán danh sách ID / chọn email Đại diện, chọn một hay nhiều tháng → Tổng Kim cương từng người, kèm mục cần xem lại.',
        keywords:
          'tra cuu kim cuong kc tong id hang loat danh sach uid tiktok email dai dien agency loc nhieu thang so sanh',
        requires: 'dataImport',
      },
      {
        key: 'revenue-management',
        icon: <TrophyOutlined />,
        label: 'Bảng xếp hạng',
        description:
          'Xem kim cương, giờ LIVE của Creator và Đối tác trên bảng xếp hạng app. Trang chỉ để xem; file TikTok nhập ở Nhập dữ liệu.',
        keywords: 'revenue doanh thu kim cuong bxh thang gio live nhap file tiktok uid',
      },
      {
        // ADMIN + MOD + Quản lý Studio (xem Studio mình) thấy mục; phạm vi thật do API /analytics quyết
        // (docs/phan-tich-creator/01-dac-ta-2026-10-06.md §8). Thiếu 'STUDIO' thì RoleGate chặn Quản lý Studio.
        key: 'analytics',
        audience: ['ADMIN', 'MOD', 'STUDIO'],
        icon: <FundProjectionScreenOutlined />,
        label: 'Phân tích',
        description:
          'Tăng/giảm, tiến độ tới bậc, Creator cần chú ý, biểu đồ theo ngày của từng Creator và nhóm.',
        keywords:
          'phan tich analytics xu huong tang giam tien do bac can chu y bieu do kim cuong gio live ngay hop le',
      },
      {
        // ADMIN + MOD xem; khối cấu hình chỉ ADMIN sửa (docs/chuc-mung-moc-kc/DAC-TA-2026-10-01.md §6).
        key: 'kc-milestones',
        icon: <GiftOutlined />,
        label: 'Chúc mừng đạt mốc',
        description:
          'Creator đạt mốc Kim cương trong tháng: lời chúc mừng trên Trang chủ app, push cho Creator, ẩn / hiện từng người.',
        keywords: 'chuc mung dat moc kim cuong kc milestone vinh danh tang giam duy tri push trang chu',
      },
      {
        // Chỉ xem, ADMIN + MOD (docs/nhap-du-lieu/DAC-TA-DOT-2-VAN-DE-DU-LIEU-2026-09-29.md §4).
        key: 'data-issues',
        icon: <WarningOutlined />,
        label: 'Vấn đề dữ liệu',
        description:
          'Creator đã rời đại lý, vắng khỏi file, đổi TikTok ID và UID chưa có tài khoản.',
        keywords:
          'van de du lieu roi dai ly vang mat doi tiktok id doi ten uid chua co tai khoan rui ro',
      },
    ],
  },
  {
    // Thưởng tháng của Creator theo chính sách hpvn.media/creator-2026 (docs/thuong-online/02-dac-ta-bang-data-creator.md).
    // Key nhóm khác key mục con — antd Menu cần không trùng.
    key: 'online-reward-center',
    label: 'Thưởng Online',
    description: 'Tự tính thưởng tháng của Creator từ file TikTok, nhập khoản tay và chốt tháng.',
    icon: <GiftOutlined />,
    tone: 'star',
    items: [
      {
        key: 'online-reward',
        icon: <CalculatorOutlined />,
        label: 'Bảng thưởng tháng',
        description:
          'Thưởng bậc, Creator mới, chuyên cần, BXH ngày và khoản tay của từng Creator; chốt tháng và xuất Excel.',
        keywords: 'thuong online bao cap bac creator luong thang kim cuong chot thang excel nhiem vu',
        requires: 'onlineReward',
      },
      {
        key: 'online-reward/items',
        icon: <UnorderedListOutlined />,
        label: 'Khoản nhập tay',
        description: 'PKQT, sinh nhật, sự kiện, lương đảm bảo, trừ ngày/giờ LIVE và VPNT — nhập rồi Admin xác nhận.',
        keywords: 'khoan tay pkqt sinh nhat su kien luong dam bao vpnt tru gio duyet',
        requires: 'onlineReward',
      },
      {
        key: 'online-reward/policy',
        icon: <SettingOutlined />,
        label: 'Chính sách',
        description: 'Bảng 27 bậc, mức thưởng, Nhóm loại trừ và người được xem / nhập.',
        keywords: 'chinh sach thuong bac muc thuong cau hinh quyen nhom loai tru',
        adminOnly: true,
      },
    ],
  },
  {
    // Phí HP Media trả Agency theo chính sách hpvn.media/agency-2026 (docs/phi-agency/01-cong-thuc-phi-agency.md).
    key: 'agency-fee-center',
    label: 'Phí Agency',
    description: 'Tự tính phí NV A–D HP Media trả cho từng Agency từ file TikTok, kèm thưởng Creator.',
    icon: <ShopOutlined />,
    tone: 'info',
    items: [
      {
        key: 'agency-fee',
        icon: <CalculatorOutlined />,
        label: 'Bảng phí tháng',
        description: 'Phí NV A, B, C, D của từng Agency, thưởng Creator, tổng thanh toán; lọc theo email Đại diện, xuất Excel.',
        keywords: 'phi agency dai ly hoa hong nhom nhiem vu tong thanh toan email dai dien excel',
        adminOnly: true,
      },
      {
        key: 'agency-fee/policy',
        icon: <SettingOutlined />,
        label: 'Chính sách',
        description: 'Tỷ lệ NV A/B/C, bảng NV D Creator mới, Agency nội bộ, email Đại diện → Agency.',
        keywords: 'chinh sach phi agency ty le nhiem vu creator moi noi bo stk mail',
        adminOnly: true,
      },
    ],
  },
  {
    // Báo giá thiết bị + hợp đồng hỗ trợ thiết bị cho Creator (docs/bao-gia-thiet-bi/01-dac-ta-2026-10-27.md).
    // KTV (Hỗ trợ LIVE) và MOD được Admin cấp; Admin luôn thấy. Key nhóm khác key mục con.
    key: 'tech-center',
    label: 'Kỹ thuật',
    description: 'Báo giá setup phòng LIVE cho Creator, xuất ảnh / Excel và hợp đồng hỗ trợ thiết bị.',
    icon: <ToolOutlined />,
    tone: 'purple',
    items: [
      {
        key: 'equipment-quotes',
        icon: <CalculatorOutlined />,
        label: 'Báo giá thiết bị',
        description:
          'Chọn máy ảnh, soundcard, PC… thành báo giá; chia phần Công ty hỗ trợ / Creator trả; xuất ảnh, Excel và hợp đồng.',
        keywords:
          'bao gia thiet bi may anh soundcard pc setup phong live ktv ky thuat hop dong ho tro creator excel anh',
        requires: 'equipmentQuote',
      },
      {
        key: 'equipment-quotes/:id',
        label: 'Soạn báo giá',
        description: 'Chọn thiết bị, chia chi phí, nhập thông tin Creator và điều khoản hợp đồng.',
        hidden: true,
        parent: 'equipment-quotes',
        requires: 'equipmentQuote',
      },
    ],
  },
  {
    key: 'live',
    label: 'LIVE & Event',
    description: 'Phòng live, PK, bình chọn và âm thanh.',
    icon: <VideoCameraOutlined />,
    tone: 'danger',
    items: [
      {
        key: 'live-management',
        icon: <VideoCameraOutlined />,
        label: 'Liveshow',
        description: 'Phòng live trên app; tab Nhóm liveshow để chia nhóm phòng.',
        keywords: 'liveshow phong live event su kien',
      },
      {
        key: 'show-management',
        label: 'Nhóm liveshow',
        description: 'Nhóm phòng live — nằm trong trang Liveshow.',
        keywords: 'group show nhom',
        hidden: true,
        tabOf: 'live-management',
      },
      {
        key: 'pk-management',
        icon: <ThunderboltOutlined />,
        label: 'PK Đại chiến',
        description: 'Trận PK giữa các Creator; tab Quốc gia để quản lý cờ dùng cho PK.',
        keywords: 'pk battle dai chien',
      },
      {
        key: 'setting-country',
        label: 'Quốc gia',
        description: 'Quốc gia và cờ dùng cho PK — nằm trong trang PK Đại chiến.',
        keywords: 'country quoc gia co',
        hidden: true,
        tabOf: 'pk-management',
      },
      {
        key: 'vote-management',
        icon: <LikeOutlined />,
        label: 'Bình chọn',
        description: 'Sự kiện tự bật trong khung giờ đã đặt; có thể tạm tắt bất cứ lúc nào.',
        keywords: 'vote binh chon su kien',
      },
      {
        key: 'add-user-vote',
        label: '+ Creator',
        description: 'Thêm Creator vào sự kiện bình chọn.',
        hidden: true,
        parent: 'vote-management',
      },
      {
        key: 'votes/:id/list-user',
        label: 'Danh sách',
        description: 'Danh sách Creator trong sự kiện bình chọn.',
        hidden: true,
        parent: 'vote-management',
      },
      {
        // Đợt 1: API /gala/admin chỉ ADMIN nên chỉ ADMIN thấy mục; MOD quét vé bằng app (docs/gala/01-dac-ta §8).
        key: 'gala',
        icon: <TrophyOutlined />,
        label: 'GALA',
        description: 'Gala cuối năm: thông tin, hạng mục, vé mời QR, check-in, nhắc lịch.',
        keywords: 'gala su kien ve moi qr',
        audience: ['ADMIN'],
      },
      {
        key: 'gala/:id',
        label: 'Sự kiện Gala',
        description: 'Thông tin, nội dung, hạng mục, vé mời, check-in và nhắc lịch của một sự kiện Gala.',
        hidden: true,
        parent: 'gala',
        audience: ['ADMIN'],
      },
      {
        key: 'sounds-management',
        icon: <SoundOutlined />,
        label: 'Âm thanh',
        description: 'Hiệu ứng âm thanh dùng trong phòng live.',
        keywords: 'sound am thanh nhac',
      },
    ],
  },
  {
    key: 'content',
    label: 'Nội dung',
    description: 'Bảng tin nội bộ, bài viết, banner nổi bật trên Trang chủ, quảng cáo và chính sách.',
    icon: <ReadOutlined />,
    tone: 'info',
    items: [
      {
        // Bảng tin nội bộ (docs/bang-tin/03-admin.md). ADMIN + MOD; tab Thành viên, Cài đặt: MOD chỉ xem.
        key: 'feed',
        icon: <ProfileOutlined />,
        label: 'Bảng tin',
        description:
          'Bài đăng nội bộ của Quản lý, Agency, Creator, Nhân viên: xử lý báo cáo trong 24 giờ, xem lại bài cần xem, xác nhận thành viên, đăng bài tên HP Media, cài đặt quyền, video, từ cấm, AI.',
        keywords:
          'feed bang tin newsfeed bai dang mang noi bo bao cao kiem duyet can xem cho duyet thanh vien nhan vien npc xac nhan bien tap trang tu cam video han che chan hp media quan trong ai',
      },
      {
        key: 'news-management',
        icon: <ReadOutlined />,
        label: 'Bài viết',
        description: 'Bài viết/tin tức hiển thị trên app.',
        keywords: 'news tin tuc bai viet',
      },
      {
        key: 'banners-management',
        icon: <PictureOutlined />,
        label: 'Banner Nổi bật',
        description: 'Banner, Tin nổi bật và HP Store — các thẻ ảnh bấm mở link trên app.',
        keywords: 'banner noi bat anh lien ket',
      },
      {
        key: 'outstanding-management',
        label: 'Tin nổi bật',
        description: 'Thẻ tin nổi bật trên Trang chủ — nằm trong trang Banner Nổi bật.',
        keywords: 'outstanding noi bat',
        hidden: true,
        adminOnly: true,
        tabOf: 'banners-management',
      },
      {
        key: 'store-management',
        label: 'HP Store',
        description: 'Sản phẩm HP Store — nằm trong trang Banner Nổi bật.',
        keywords: 'store cua hang san pham',
        hidden: true,
        tabOf: 'banners-management',
      },
      {
        key: 'setting-ads',
        icon: <FundProjectionScreenOutlined />,
        label: 'Quảng cáo',
        description: 'Ảnh quảng cáo bật lên khi mở app; hẹn giờ chạy/tắt từng ảnh.',
        keywords: 'ads quang cao popup lich chay hen gio',
        // /image-ads chỉ cho ADMIN (RolesGuard trả 401 → MOD sẽ bị đăng xuất nếu vào).
        adminOnly: true,
      },
      {
        key: 'policy-manager',
        icon: <FileProtectOutlined />,
        label: 'Chính sách HP',
        description: 'Quy tắc cộng đồng, giới thiệu, pháp lý, thông tin ứng dụng, liên hệ.',
        keywords: 'policy chinh sach quy dinh rules phap ly',
      },
    ],
  },
  {
    key: 'system',
    label: 'Hệ thống',
    description: 'Gửi thông báo, lưới Ứng dụng và cài đặt chung.',
    icon: <SettingOutlined />,
    tone: 'warning',
    items: [
      {
        key: 'notification-management',
        icon: <NotificationOutlined />,
        label: 'Thông báo',
        description: 'Thông báo đẩy tới người dùng app; tab Tin nhắn nhóm tự động.',
        keywords: 'notification push thong bao gui',
        adminOnly: true,
      },
      {
        key: 'job-management',
        label: 'Tin nhắn nhóm tự động',
        description:
          'Tin nhắn gửi vào nhóm chat: gửi ngay, đặt lịch hoặc gửi thủ công — nằm trong trang Thông báo.',
        keywords: 'job notification tin nhan tu dong',
        hidden: true,
        adminOnly: true,
        tabOf: 'notification-management',
      },
      {
        key: 'menus-management',
        icon: <AppstoreOutlined />,
        label: 'Ứng dụng',
        description: 'Lưới Ứng dụng trên app: nhóm, ghim Trang chủ, ai thấy, link.',
        keywords: 'menu ung dung app link',
      },
      {
        key: 'setting-management',
        icon: <SettingOutlined />,
        label: 'Cài đặt chung',
        description: 'Chat & nhóm, chấm công & lương, thông báo định kỳ, bật/tắt tính năng.',
        keywords: 'setting cai dat chat chinh sach nhan tin',
      },
      {
        key: 'watermark',
        icon: <SafetyCertificateOutlined />,
        label: 'Watermark',
        description:
          'Chữ mờ tên + mã truy vết phủ mọi màn hình app và trang Quản trị; tra mã, soi ảnh bị lộ ra người chụp.',
        keywords: 'watermark chong lo lot thong tin chup man hinh leak tra ma soi anh',
      },
    ],
  },
];

/** Biểu tượng cho mục "Tổng quan". */
export const HOME_ICON = <DashboardOutlined />;

export interface NavMatch {
  item: NavItem;
  group?: NavGroup;
  parentItem?: NavItem;
}

/** So khớp `pathname` với mục menu, hiểu cả tham số kiểu `group/:id/management`. */
export function findNav(pathname: string): NavMatch | undefined {
  const clean = pathname.replace(/^\/+|\/+$/g, '');
  if (!clean) return { item: HOME_ITEM };
  for (const group of NAV_GROUPS) {
    for (const item of group.items) {
      const pattern = new RegExp(`^${item.key.replace(/:[^/]+/g, '[^/]+')}$`);
      if (pattern.test(clean)) {
        const parentKey = item.parent ?? item.tabOf;
        const parentItem = parentKey
          ? group.items.find((candidate) => candidate.key === parentKey)
          : undefined;
        return { item, group, parentItem };
      }
    }
  }
  return undefined;
}

/** Quyền riêng của người đang đăng nhập, dùng cho mục có `requires`. */
export interface NavPerms {
  dataImport?: boolean;
  onlineReward?: boolean;
  equipmentQuote?: boolean;
}

// Mục có `requires` chỉ qua khi quyền tương ứng là `true`; không truyền `perms` → coi như chưa có.
const hasRequired = (
  item: NavItem,
  perms?: NavPerms,
  isAdmin = false,
  viewer?: NavAudience | null
) =>
  !item.requires ||
  perms?.[item.requires] === true ||
  !!item.requiresExcept?.includes(viewer ?? (isAdmin ? 'ADMIN' : 'MOD'));

/**
 * Mục có `audience` chỉ hiện với người xem nằm trong danh sách; mục không khai thì Quản lý Studio
 * không thấy, người khác theo `adminOnly` / `requires` như cũ. `viewer` không truyền → suy từ
 * `isAdmin` ('ADMIN' | 'MOD'), nên mọi nơi gọi cũ chạy y như trước.
 */
export function allowsAudience(item: NavItem, isAdmin: boolean, viewer?: NavAudience | null) {
  const who: NavAudience = viewer ?? (isAdmin ? 'ADMIN' : 'MOD');
  if (item.audience) return item.audience.includes(who);
  return who !== 'STUDIO';
}

export function isVisibleFor(
  item: NavItem,
  isAdmin: boolean,
  perms?: NavPerms,
  viewer?: NavAudience | null
) {
  return (
    !item.hidden &&
    (!item.adminOnly || isAdmin) &&
    hasRequired(item, perms, isAdmin, viewer) &&
    allowsAudience(item, isAdmin, viewer)
  );
}

/** Cho ô "Tìm chức năng": gồm cả các trang cũ nay là tab. */
export function isSearchableFor(
  item: NavItem,
  isAdmin: boolean,
  perms?: NavPerms,
  viewer?: NavAudience | null
) {
  return (
    (!item.hidden || !!item.tabOf) &&
    (!item.adminOnly || isAdmin) &&
    hasRequired(item, perms, isAdmin, viewer) &&
    allowsAudience(item, isAdmin, viewer)
  );
}

/** Cho RoleGate: người xem có được mở trang này không (tính cả trang `hidden`). */
export function isPathAllowedFor(
  pathname: string,
  isAdmin: boolean,
  perms?: NavPerms,
  viewer?: NavAudience | null
) {
  const item = findNav(pathname)?.item;
  return (
    !!item &&
    (!item.adminOnly || isAdmin) &&
    hasRequired(item, perms, isAdmin, viewer) &&
    allowsAudience(item, isAdmin, viewer)
  );
}

/** Tìm mục menu theo key (dùng cho lối tắt ở Tổng quan). */
export function navItem(key: string): NavItem | undefined {
  for (const group of NAV_GROUPS) {
    const found = group.items.find((item) => item.key === key);
    if (found) return found;
  }
  return undefined;
}
